import { createApi, defineEndpoints, fetcher } from '@blockworks/platform/api';

import { REQUEST_HEADER_NAME } from '@/api/csrf/options';

const SHARE_ENDPOINTS = defineEndpoints(template => ({
    POST: {
        CREATE_SHARE_PAGE: template('/api/share/create-share-page'),
        UPLOAD_TOKEN: template('/api/share/upload-token'),
        UPLOAD_IMAGE: template('/v3/upload/screenshots/visualizations'),
    },
}));

type UploadImageRequest = {
    uploadToken: string;
    image: Blob;
};
type UploadImageResponse = { name: string; mime_type: string; metadata: Record<string, string> };

type UploadTokenResponse = {
    uploadToken: string;
};

type UploadTokenRequest = {
    metadata: Record<string, string>;
    token: string;
};

type CreateSharePageRequest = {
    slug: string;
    token: string;
};

type CreateSharePageResponse = {
    path: string;
    success: boolean;
};

const shareApi = createApi(build => ({
    post: {
        createSharePage: build<CreateSharePageResponse, CreateSharePageRequest>({
            cachePrefix: 'render-share-page',
            baseUrl: 'same-origin',
            query: ({ token, slug }) => ({
                uri: SHARE_ENDPOINTS.POST.CREATE_SHARE_PAGE(),
                options: {
                    method: fetcher.Method.Post,
                    body: { slug },
                    headers: {
                        [REQUEST_HEADER_NAME]: token,
                    },
                },
            }),
        }),
        uploadToken: build<UploadTokenResponse, UploadTokenRequest>({
            cachePrefix: 'upload-token',
            baseUrl: 'same-origin',
            query: ({ token, metadata }) => ({
                uri: SHARE_ENDPOINTS.POST.UPLOAD_TOKEN(),
                options: {
                    method: fetcher.Method.Post,
                    body: metadata,
                    headers: {
                        [REQUEST_HEADER_NAME]: token,
                    },
                },
            }),
        }),
        uploadImage: build<UploadImageResponse, UploadImageRequest>({
            useResearchApiV2Mapper: true,
            cachePrefix: 'upload-image',
            baseUrl: 'research-api-v2',
            query: ({ uploadToken, image }) => {
                const formData = new FormData();
                formData.append(`file`, image);

                return {
                    uri: SHARE_ENDPOINTS.POST.UPLOAD_IMAGE(),
                    options: {
                        method: build.Method.Post,
                        body: formData,
                        headers: {
                            Authorization: `Bearer ${uploadToken}`,
                        },
                    },
                };
            },
        }),
    },
}));

export { shareApi };
